import authenticated from "@/router/guards/authenticated";
import guest from "@/router/guards/guest";
import masqueradeOnly from "@/router/guards/masqueradeOnly";
import native from "@/router/guards/native";
import setup from "@/router/guards/setup";
import {useMainStore} from "@/store";
import { isPlatform } from "@ionic/vue";

export const routes = [
    /*
     * Base
     */
    {
        path: '/',
        redirect: isPlatform('android') || isPlatform('ios') ? '/auth/register' : '/auth/login',
    },
    /*
     * Authentication Routes
     */
    {
        path: '/auth',
        component: () => import('@/components/Containers/Guest'),
        children: [
            {
                path: '',
                redirect: '/auth/register',
            },
            {
                path: 'login',
                name: 'login',
                component: () => import('@/views/Guest/MasqueradeLogin.vue'),
                beforeEnter: [guest],
                meta: {
                    title: 'Masquerade as User',
                },
            },
            {
                path: 'masquerade',
                name: 'masquerade',
                component: () => import('@/views/Guest/MasqueradeToken.vue'),
                meta: {
                    title: 'Masquerade as User',
                    dynamicTitle: true,
                },
            },
            {
                path: 'register',
                name: 'register',
                component: () => import('@/views/Guest/Register'),
                beforeEnter: [native, guest],
                meta: {
                    title: 'Register',
                },
            },
            {
                path: 'vpn-required',
                name: 'vpn-required',
                component: () => import('@/views/Guest/VPN'),
                meta: {
                    title: 'Network Access Required',
                },
            },
            {
                path: 'maintenance',
                name: 'maintenance',
                component: () => import('@/views/Guest/Maintenance'),
                meta: {
                    title: 'Under Maintenance',
                },
            },
            {
                path: 'banned',
                name: 'banned',
                component: () => import('@/views/Guest/Banned'),
                meta: {
                    title: 'Account Banned',
                },
            },
        ],
    },
    /*
     * Account Setup Routes
     */
    {
        path: '/setup',
        component: () => import('@/components/Containers/Setup'),
        beforeEnter: [authenticated],
        children: [
            {
                path: '',
                redirect: '/setup/character-select',
            },
            {
                path: 'character-select',
                name: 'character-select',
                component: () => import('@/views/Setup/CharacterSelect'),
                meta: {
                    title: 'Choose Your Character',
                }
            },
            {
                path: 'coppa',
                name: 'coppa',
                component: () => import('@/views/Setup/Coppa'),
                meta: {
                    title: 'Age Verification',
                }
            },
        ],
    },
    /*
     * Tutorial Route (full-screen, outside setup card)
     */
    {
        path: '/tutorial',
        name: 'tutorial',
        component: () => import('@/views/Setup/Tutorial.vue'),
        beforeEnter: [authenticated],
        meta: {
            title: 'Tutorial',
        }
    },
    /*
     * Application Routes
     */
    {
        path: '/app',
        component: () => import('@/components/Containers/Authenticated'),
        beforeEnter: [authenticated, setup],
        children: [
            {
                path: '',
                redirect: '/app/dashboard',
            },
            {
                path: 'dashboard',
                name: 'dashboard',
                component: () => import('@/views/Authenticated/Dashboard'),
                meta: {
                    title: 'Dashboard',
                },
            },
            {
                path: 'characters',
                name: 'characters',
                component: () => import('@/views/Authenticated/Characters'),
                meta: {
                    title: 'Characters',
                },
            },
            {
                path: 'character/:id',
                redirect: to => ({
                    name: 'character',
                    params: {
                        id: to.params.id,
                        tab: 'details',
                    },
                }),
            },
            {
                path: 'character/:id/:tab',
                name: 'character',
                component: () => import('@/views/Authenticated/Character/Character'),
                meta: {
                    title: 'Character Details',
                },
            },
            {
                path: 'locker',
                name: 'locker',
                component: () => import('@/views/Authenticated/Locker'),
                meta: {
                    title: 'Locker',
                },
            },
            {
                path: 'guild',
                redirect: () => ({
                    name: 'guild',
                    params: {
                        tab: useMainStore().guilded ? 'details' : 'search',
                    },
                }),
            },
            {
                path: 'guild/:tab',
                name: 'guild',
                component: () => import('@/views/Authenticated/Guild/Guild'),
                meta: {
                    title: 'Guild',
                },
            },
            {
                path: 'achievements',
                name: 'achievements',
                component: () => import('@/views/Authenticated/Achievements'),
                meta: {
                    title: 'Achievements',
                },
            },
            {
                path: 'store',
                name: 'store',
                component: () => import('@/views/Authenticated/Store/Store'),
                meta: {
                    title: 'Store',
                },
            },
            {
                path: 'settings',
                redirect: '/app/settings/general',
            },
            {
                path: 'settings/:tab',
                name: 'settings',
                component: () => import('@/views/Authenticated/Settings/Settings'),
                meta: {
                    title: 'Settings',
                },
            },
            {
                path: 'guide',
                redirect: () => ({
                    name: 'guide',
                    params: {
                        tab: 'characters'
                    },
                }),
            },
            {
                path: 'guide/:tab',
                name: 'guide',
                component: () => import('@/views/Authenticated/Guide/Guide'),
                meta: {
                    title: 'Progression Guide',
                },
            },
            {
                path: 'notifications',
                name: 'notifications',
                component: () => import('@/views/Authenticated/Notifications/Notifications'),
                meta: {
                    title: 'Notifications',
                },
            },
            /*
             * Game Routes
             */
            {
                path: '/app/game',
                component: () => import('@/views/Authenticated/Game/Game'),
                children: [
                    {
                        path: '',
                        name: 'game',
                        redirect: '/app/game/lobby',
                    },
                    {
                        path: '/app/game/lobby',
                        name: 'lobby',
                        meta: {
                            title: 'Game Lobby',
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/Lobby'),
                    },
                    {
                        path: '/app/game/divisions',
                        name: 'divisions',
                        meta: {
                            title: 'Divisions',
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/Divisions'),
                    },
                    {
                        path: '/app/game/character',
                        name: 'choose-character',
                        meta: {
                            title: 'Choose Character',
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/Character'),
                    },
                    {
                        path: '/app/game/difficulty',
                        name: 'difficulty',
                        meta: {
                            title: 'Bout Difficulty',
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/Difficulty'),
                    },
                    {
                        path: '/app/game/preview',
                        name: 'preview',
                        meta: {
                            title: 'Bout Preview',
                        },
                        component: () => import ('@/views/Authenticated/Game/Pages/Preview'),
                    },
                    {
                        path: '/app/game/bout',
                        name: 'bout',
                        meta: {
                            title: 'Bout',
                            hideChrome: true,
                        },
                        component: () => import ('@/views/Authenticated/Game/Pages/Bout'),
                    },
                    {
                        path: '/app/game/scoreboard',
                        name: 'scoreboard',
                        meta: {
                            title: 'Bout Scoreboard',
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/Scoreboard'),
                    },
                    {
                        path: '/app/game/ad-break',
                        name: 'ad-break',
                        meta: {
                            title: 'Sponsored Break',
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/AdBreak'),
                    },
                    {
                        path: '/app/game/division-complete',
                        name: 'division-complete',
                        meta: {
                            title: 'Division Complete',
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/DivisionComplete'),
                    },
                    {
                        path: '/app/game/game-over',
                        name: 'game-over',
                        meta: {
                            title: 'Game Over',
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/GameOver'),
                    },



                    {
                        path: 'practice',
                        redirect: () => ({
                            name: 'practice',
                            params: {
                                tab: 'avoid'
                            },
                        }),
                    },
                    {
                        path: '/app/game/practice/drill/:abilityId/:drillType',
                        name: 'drill',
                        meta: {
                            title: 'Practice Drill',
                            hideChrome: true,
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/Practice/Pages/Drill'),
                    },
                    {
                        path: '/app/game/practice/:tab',
                        name: 'practice',
                        meta: {
                            title: 'Practice Arena',
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/Practice/Practice'),
                    },

                    {
                        path: '/app/game/multi-player',
                        name: 'matchmaking',
                        meta: {
                            title: 'Matchmaking',
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/Matchmaking'),
                    },
                    {
                        path: '/app/game/locker-room',
                        name: 'locker-room',
                        meta: {
                            title: 'Bout Preview',
                        },
                        component: () => import('@/views/Authenticated/Game/Pages/LockerRoom'),
                    },
                ],
            },
        ],
    },
    /*
     * Bout Replay (top-level — no Authenticated container wrapper)
     */
    {
        path: '/app/replay/:boutId',
        name: 'bout-replay',
        meta: {
            title: 'Bout Replay',
        },
        component: () => import('@/views/Authenticated/Game/Pages/BoutReplay'),
        beforeEnter: [masqueradeOnly],
    },
    /*
     * Logout
     */
    {
        path: '/logout',
        name: 'logout',
        component: () => import('@/views/Logout'),
    },
    /*
     * Not Found
     */
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/NotFound')
    },
];
